/**
 * UV atlas for the authored characters (plan 05 P5.2, Design 2).
 *
 * The soldier and the boss are each ONE merged mesh with ONE material, so every primitive has to
 * carry its own corner of a shared texture space: after creating a part and BEFORE the merge we
 * remap its 0..1 UVs into its assigned tile. Painted later by the procedural PBR materials
 * (P5.5) — this module is the SINGLE SOURCE for the tile rects, so geometry and material cannot
 * drift apart (the plan-05 P5.5 test asserts the shared table).
 *
 * Layout: 2 columns × 6 rows of equal tiles (uniform tile size ⇒ uniform texel density, which is
 * what makes a DynamicTexture painted per-tile read consistently). The top 3 rows (v ∈ [0.5, 1])
 * carry the six soldier tiles; the bottom 3 rows (v ∈ [0, 0.5]) are the boss tiles (P5.4 reuses
 * this table rather than defining a second one).
 *
 * Determinism: no RNG anywhere in here — literal arithmetic constants only.
 */
import { VertexBuffer } from '@babylonjs/core/pure';
import type { Mesh } from '@babylonjs/core/pure';

export const ATLAS_COLS = 2;
export const ATLAS_ROWS = 6;

/**
 * Inset applied when remapping, so a part's UVs sit strictly INSIDE its tile: bilinear filtering
 * (and mip chains, P5.5) must never sample the neighbouring tile. 1/256 of the atlas is ≈2 texels
 * on a 512² atlas.
 */
export const ATLAS_PAD = 1 / 256;

export type AtlasTile =
  // soldier (rows 0..2)
  | 'uniform'
  | 'skin'
  | 'helmet'
  | 'weapon'
  | 'gear'
  | 'accent'
  // boss (rows 3..5, P5.4)
  | 'bossPlate'
  | 'bossHide'
  | 'bossClub'
  | 'bossTrim'
  | 'bossBone'
  // boss emissive slits (plan 08 G5): fills the spare 12th cell; the emissive map lights ONLY this tile
  | 'bossGlow';

/** Grid position of every tile. Row 0 is the TOP of the texture (v closest to 1). */
export const TILE_GRID: Record<AtlasTile, { col: number; row: number }> = {
  uniform: { col: 0, row: 0 },
  skin: { col: 1, row: 0 },
  helmet: { col: 0, row: 1 },
  weapon: { col: 1, row: 1 },
  gear: { col: 0, row: 2 },
  accent: { col: 1, row: 2 },
  bossPlate: { col: 0, row: 3 },
  bossHide: { col: 1, row: 3 },
  bossClub: { col: 0, row: 4 },
  bossTrim: { col: 1, row: 4 },
  bossBone: { col: 0, row: 5 },
  bossGlow: { col: 1, row: 5 },
};

export interface TileRect {
  u0: number;
  v0: number;
  u1: number;
  v1: number;
}

/**
 * The full tile rect (the region the material paints). Babylon texture UVs have v = 0 at the
 * bottom of the image, so row 0 maps to the band just under v = 1.
 */
export function tileRect(tile: AtlasTile): TileRect {
  const { col, row } = TILE_GRID[tile];
  const du = 1 / ATLAS_COLS;
  const dv = 1 / ATLAS_ROWS;
  return {
    u0: col * du,
    v0: 1 - (row + 1) * dv,
    u1: (col + 1) * du,
    v1: 1 - row * dv,
  };
}

/** The rect the geometry actually maps into: the tile shrunk by ATLAS_PAD on every side. */
export function tileUvBounds(tile: AtlasTile): TileRect {
  const r = tileRect(tile);
  return { u0: r.u0 + ATLAS_PAD, v0: r.v0 + ATLAS_PAD, u1: r.u1 - ATLAS_PAD, v1: r.v1 - ATLAS_PAD };
}

/**
 * Remap a freshly-built primitive's UVs into `tile`, in place, BEFORE the merge.
 *
 * The source range is MEASURED, not assumed: box/sphere/cylinder emit 0..1, but the capsule
 * builder does not (its v runs from ≈ −0.5 to ≈ 0.75), so a plain scale+offset would spill part of
 * every limb into the neighbouring tile and the atlas containment assertion would fail. Normalising
 * first means "this part fills its tile" for every primitive Babylon gives us.
 *
 * Writing the buffer back updatable keeps the attribute Float32 (a half-float UV buffer would
 * quantise tile offsets and blur the containment assertion).
 */
export function remapUVs(mesh: Mesh, tile: AtlasTile): void {
  const uv = mesh.getVerticesData(VertexBuffer.UVKind);
  if (!uv) return; // a primitive without UVs has nothing to atlas (and cannot be textured)
  let minU = Infinity;
  let maxU = -Infinity;
  let minV = Infinity;
  let maxV = -Infinity;
  for (let i = 0; i < uv.length; i += 2) {
    const u = uv[i]!;
    const v = uv[i + 1]!;
    if (u < minU) minU = u;
    if (u > maxU) maxU = u;
    if (v < minV) minV = v;
    if (v > maxV) maxV = v;
  }
  const r = tileUvBounds(tile);
  const su = r.u1 - r.u0;
  const sv = r.v1 - r.v0;
  // A degenerate axis (flat geometry, e.g. a one-sided plate) parks on the tile centre instead of
  // dividing by zero.
  const ru = maxU - minU > 1e-9 ? su / (maxU - minU) : 0;
  const rv = maxV - minV > 1e-9 ? sv / (maxV - minV) : 0;
  const ou = ru === 0 ? r.u0 + su / 2 : r.u0 - minU * ru;
  const ov = rv === 0 ? r.v0 + sv / 2 : r.v0 - minV * rv;
  for (let i = 0; i < uv.length; i += 2) {
    uv[i] = ou + uv[i]! * ru;
    uv[i + 1] = ov + uv[i + 1]! * rv;
  }
  mesh.setVerticesData(VertexBuffer.UVKind, uv, true);
}

// ---------------------------------------------------------------------------
// Authored-dimension constants (plan 05 P5.2): the geometry contract that other
// layers (the framing probe, the material painter) must agree with.
// ---------------------------------------------------------------------------

/** Vertex budget for the authored soldier (plan 05 Design 2 / §P5.2). */
export const SOLDIER_TRI_BUDGET = 1100;
/** Vertex budget for the authored boss (§P5.4). */
export const BOSS_TRI_BUDGET = 8000;

/**
 * Height of the head sample used by `Squad.debugFraming` (was the capsule mannequin's 1.18 head
 * centre, actors.ts:163 before P5.2). This is the authored face centre: the framing contract
 * ("head from top ≤ 0.75") samples the front row here, so it must track the mesh, not the old
 * placeholder.
 */
export const SOLDIER_HEAD_Y = 1.6;
/** Waist/belt height, used by the framing edge sample (unchanged 0.6 by contract). */
export const SOLDIER_WAIST_Y = 0.6;
